import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * SocialInstagram icon component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconSocialInstagram extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M6.2 23.1H17.9C20.8 23.1 23.1 20.8 23.2 18V6.2C23.2 3.3 20.8 1 18 1H6.2C3.3 1 1 3.3 1 6.2V17.9C1 20.8 3.4 23.1 6.2 23.1ZM2.5 6.2C2.5 4.1 4.2 2.5 6.2 2.5H18C20.1 2.5 21.7 4.2 21.7 6.2V18C21.7 20.1 20 21.7 18 21.7H6.2C4.1 21.7 2.5 20 2.5 18V6.2ZM7 12.1C7 14.9 9.3 17.2 12.1 17.2C14.9 17.2 17.2 14.9 17.2 12.1C17.2 9.3 14.9 7 12.1 7C9.3 7 7 9.3 7 12.1ZM8.5 12C8.5 10 10.1 8.4 12.1 8.4C14.1 8.4 15.7 10 15.7 12C15.7 14 14.1 15.6 12.1 15.6C10.1 15.6 8.5 14 8.5 12ZM17.0996 6.19961C17.0996 6.69961 17.4996 7.09961 17.9996 7.09961C18.5996 7.09961 18.9996 6.69961 18.9996 6.09961C18.9996 5.59961 18.5996 5.09961 17.9996 5.09961C17.4996 5.09961 17.0996 5.49961 17.0996 5.99961V6.09961V6.19961Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-social-instagram') === undefined) {
  customElements.define('cat-icon-social-instagram', BlocksIconSocialInstagram);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-social-instagram': BlocksIconSocialInstagram;
  }
}
